import Konva from 'konva/konva'
import XScaleLayer from './x-scale-layer'
import DaySeparatorsLayer from './day-separators-layer'
import VariableLayer from './variable-layer'
import TitleLayer from './title-layer'
import ResizeHandleLayer from './resize-handle-layer'
import YScaleLayer, { width as yScaleWidth } from './y-scale-layer'
import VerticalLine from './vertical-line'
import EventLayer from './event-layer'

const initialYOffset = 20
const firstVariableYOffset = 5

export default class Stage extends Konva.Stage {
  constructor ({ container, moveTime }) {
    super({
      container,
      draggable: true,
      dragBoundFunc: ({ x }) => ({ x, y: this.getAbsolutePosition().y }) // horizontal movement only
    })
    Object.assign(this, { container, moveTime })

    this._initLayers()

    this._fitWidth()

    this._resizeEventListener = this._fitWidth.bind(this)
    window.addEventListener('resize', this._resizeEventListener)

    this.on('dragstart', this._onDragStart)
    this.on('dragend', this._onDragEnd)
    this.on('click', this._onClick)
  }

  destroy () {
    window.removeEventListener('resize', this._resizeEventListener)
    super.destroy()
  }

  init ({ data, userData, timeFrom, timeTo, now }) {
    Object.assign(this, { data, userData, timeFrom, timeTo, now })

    this.render()
  }

  _fitWidth () {
    if (this.timeout) {
      window.clearTimeout(this.timeout)
    }
    // TODO: delay required by Vuetify, slow comp?
    return new Promise(resolve => {
      this.timeout = window.setTimeout(resolve, 200)
    })
      .then(() => {
        const { offsetWidth } = this.container
        if (offsetWidth > 0 && offsetWidth !== this.width()) {
          this.width(offsetWidth)
          this.render()
        }
      })
  }

  _initLayers () {
    const layers = []
    layers.push(new XScaleLayer({ name: 'xScale' }))
    layers.push(new VariableLayer({ name: 'variables', redraw: this.render.bind(this) }))
    layers.push(new EventLayer({ name: 'events' }))
    layers.push(new DaySeparatorsLayer({ name: 'daySeparators' }))
    layers.push(new TitleLayer({ name: 'titles' }))
    layers.push(new ResizeHandleLayer({ name: 'resizeHandles' }))
    layers.push(new YScaleLayer({ name: 'yScale' }))
    layers.push(new VerticalLine({ name: 'verticalLine' }))
    layers.forEach(layer => this.add(layer))
  }

  _getLayer (name) {
    return [...this.children].find(layer => layer.attrs.name === name)
  }

  render () {
    this.eventsHeight = this._getLayer('events').height
    this.yVariablesOffset = this.yOffset = initialYOffset + this.eventsHeight + firstVariableYOffset

    this._initVariables()
    this._initTitles()
    this._initResizeHandles()
    this._initYScale()
    this._initDaySeparators()
    this._initEvents()
    this._initXScale()
    this._initVerticalLine()

    this.height(this.yOffset)
  }

  _getVariablesWidth () {
    return Math.max(0, this.content.clientWidth - yScaleWidth)
  }

  _initVariables () {
    const { yOffset, timeFrom, timeTo, now, data, userData } = this
    const layer = this._getLayer('variables')

    layer.init({ width: this._getVariablesWidth(), yOffset, timeFrom, timeTo, now, data, userData })

    this.yOffset = layer.yOffset
    this.titleData = layer.titleData
    this.resizeHandleData = layer.resizeHandleData
    this.yScaleData = layer.yScaleData
    this.variables = layer.variables
  }

  _initTitles () {
    const { titleData } = this
    const layer = this._getLayer('titles')
    layer.init({ titleData })
  }

  _initResizeHandles () {
    const { resizeHandleData } = this
    const layer = this._getLayer('resizeHandles')
    layer.init({ resizeHandleData })
  }

  _initYScale () {
    const { content, yScaleData } = this
    const layer = this._getLayer('yScale')
    layer.init({
      stageWidth: content.clientWidth,
      height: this.yOffset - 1,
      yScaleData
    })
  }

  _initEvents () {
    const { yOffset, timeFrom, timeTo, data } = this
    this._getLayer('events').init({
      width: this._getVariablesWidth(),
      yFrom: initialYOffset,
      yTo: yOffset,
      timeFrom,
      timeTo,
      data,
      setFixedTime: this.setFixedTime.bind(this)
    })
  }

  _initXScale () {
    const { timeFrom, timeTo, yOffset } = this
    const layer = this._getLayer('xScale')

    layer.init({ timeFrom, timeTo, yOffset, width: this._getVariablesWidth() })

    this.xScale = layer.xScale
    this.yOffset = layer.yOffset
  }

  _initDaySeparators () {
    const { timeFrom, timeTo, yVariablesOffset, yOffset } = this
    const layer = this._getLayer('daySeparators')

    layer.init({ timeFrom, timeTo, yFrom: yVariablesOffset, yTo: yOffset, width: this._getVariablesWidth() })
  }

  _initVerticalLine () {
    const { container, yOffset, eventsHeight, timeFrom, timeTo, now, xScale, variables } = this
    this._getLayer('verticalLine').init({
      container,
      width: this._getVariablesWidth(),
      yFrom: initialYOffset + eventsHeight,
      yTo: yOffset,
      timeFrom,
      timeTo,
      now,
      xScale,
      variables
    })
  }

  setFixedTime (fixedTime) {
    this._getLayer('verticalLine').setFixedTime(fixedTime)
  }

  _onDragStart () {
    this._getLayer('verticalLine').hide()
    this._getLayer('titles').hide()
  }

  _onDragEnd () {
    this._getLayer('verticalLine').show()
    this._getLayer('titles').show()
    this.moveTime(this.x())
    // move the stage back
    this.x(0)
  }

  _onClick (event) {
    this._getLayer('verticalLine').onClick(event)
  }
}
